// AuthContext - Authentication Provider (supports both local backend and Firebase production)
import { createContext, useContext, useState, useEffect } from 'react';
import { signInWithPopup, signInWithEmailAndPassword, createUserWithEmailAndPassword, updateProfile, onAuthStateChanged } from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import { getCurrentUser, login as apiLogin, register as apiRegister, logout as apiLogout, loginWithGoogleToken, linkAnonymousSessions, setToken, getToken } from '../services/api';

// Check if we're using Firebase Functions (production) or local backend
// Production: VITE_API_URL is empty or undefined (uses relative /api/* paths via Firebase Hosting rewrites)
// Local dev: VITE_API_URL points to localhost:8080
const isFirebaseProduction = !import.meta.env.VITE_API_URL || !import.meta.env.VITE_API_URL.includes('localhost');

const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load user on mount
  useEffect(() => {
    let unsubscribe = null;

    async function loadUser() {
      try {
        if (isFirebaseProduction) {
          // For production: listen to Firebase Auth state
          unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
              const idToken = await firebaseUser.getIdToken();
              setToken(idToken);
              setUser({
                id: firebaseUser.uid,
                email: firebaseUser.email,
                displayName: firebaseUser.displayName,
                photoUrl: firebaseUser.photoURL,
                role: 'user'
              });
            } else {
              setUser(null);
            }
            setLoading(false);
          });
        } else {
          // For local backend: check stored JWT
          const currentUser = await getCurrentUser();
          setUser(currentUser);
          setLoading(false);
        }
      } catch (error) {
        console.error('Failed to load user:', error);
        setUser(null);
        setLoading(false);
      }
    }

    loadUser();

    // Listen for logout events (from API on 401)
    const handleLogout = () => {
      setUser(null);
    };
    window.addEventListener('auth:logout', handleLogout);

    return () => {
      window.removeEventListener('auth:logout', handleLogout);
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const login = async (email, password) => {
    if (isFirebaseProduction) {
      // For Firebase production: use Firebase Auth directly
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const idToken = await userCredential.user.getIdToken();
      setToken(idToken);
      const firebaseUser = {
        id: userCredential.user.uid,
        email: userCredential.user.email,
        displayName: userCredential.user.displayName,
        photoUrl: userCredential.user.photoURL,
        role: 'user'
      };
      setUser(firebaseUser);
      linkAnonymousSessions().catch(console.error);
      return { success: true, user: firebaseUser };
    } else {
      // For local backend: use API
      const response = await apiLogin({ email, password });
      setUser(response.user);
      linkAnonymousSessions().catch(console.error);
      return response;
    }
  };

  const signup = async (email, password, displayName, referralCode) => {
    if (isFirebaseProduction) {
      // For Firebase production: use Firebase Auth directly
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      // Update display name if provided
      if (displayName) {
        await updateProfile(userCredential.user, { displayName });
      }
      const idToken = await userCredential.user.getIdToken();
      setToken(idToken);
      const firebaseUser = {
        id: userCredential.user.uid,
        email: userCredential.user.email,
        displayName: displayName || userCredential.user.displayName,
        photoUrl: userCredential.user.photoURL,
        role: 'user'
      };
      setUser(firebaseUser);
      linkAnonymousSessions().catch(console.error);
      return { success: true, user: firebaseUser };
    } else {
      // For local backend: use API
      const response = await apiRegister({ email, password, displayName, referralCode });
      setUser(response.user);
      linkAnonymousSessions().catch(console.error);
      return response;
    }
  };

  const logout = async () => {
    if (isFirebaseProduction) {
      // Sign out from Firebase Auth
      await auth.signOut();
    }
    await apiLogout();
    setUser(null);
  };

  // Google OAuth - uses Firebase Auth
  const signInWithGoogle = async () => {
    const result = await signInWithPopup(auth, googleProvider);
    const idToken = await result.user.getIdToken();

    if (isFirebaseProduction) {
      // For Firebase production: store Firebase ID token directly
      setToken(idToken);
      const firebaseUser = {
        id: result.user.uid,
        email: result.user.email,
        displayName: result.user.displayName,
        photoUrl: result.user.photoURL,
        role: 'user'
      };
      setUser(firebaseUser);
      linkAnonymousSessions().catch(console.error);
      return { success: true, user: firebaseUser };
    } else {
      // For local backend: exchange token for backend JWT
      const response = await loginWithGoogleToken(idToken, {
        email: result.user.email,
        displayName: result.user.displayName,
        photoUrl: result.user.photoURL
      });
      setUser(response.user);
      linkAnonymousSessions().catch(console.error);
      return response;
    }
  };

  const loginWithGoogle = signInWithGoogle;

  const value = {
    user,
    loading,
    login,
    signup,
    loginWithGoogle,
    signInWithGoogle,
    logout,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
